import { useState } from 'react';
import FadeInSection from '../../../components/feature/FadeInSection';

interface FaqItem {
  id: number;
  question: string;
  answer: string;
}

const faqItems: FaqItem[] = [
  {
    id: 1,
    question: '入会をしたいのですが、年齢制限はありますか？',
    answer:
      '真剣に人生のパートナーをお探しの方でしたら、年齢制限なしでどなたでもご入会いただけます。',
  },
  {
    id: 2,
    question: '無料相談はどこで行っていますか？',
    answer:
      'オンライン（ZOOM）、LINE電話または都内・近郊地域の方はホテルラウンジなどにて直接お会いして行うことが可能です。\n※ご自宅にお伺いすることはできませんので、ご了承ください。\n所要時間としては、約60分間を予定しています。',
  },
  {
    id: 3,
    question: '入会に必要な書類は何を用意すればよいですか？',
    answer:
      'ご準備いただくものは下記となります。\n\n・独身証明書、または戸籍謄本、戸籍抄本（3ヶ月以内に発行された原本）\n・卒業証明書原本または卒業証書の写し（短大卒以上の方）\n・身分証明書（運転免許証 or 保険証のコピー）\n・住民票、または光熱費の明細（3か月以内に発行された原本）\n・収入証明書（直近の源泉徴収票や確定申告書などの写し）\n・医師、弁護士、その他国家資格それに準ずる資格をお持ちの場合は、その証明書の写し\n・写真（契約締結後に、おススメのフォトスタジオをご案内可能です）',
  },
  {
    id: 4,
    question: '説明を聞いてみたいけど、無理矢理入会させられることはありませんか？',
    answer:
      '無理な勧誘は行っていません。\n先ずはお話を聞いてみたいという方がほとんどなので、サービス内容を十分ご理解いただくことを基本としています。ご自身が入会したいという気持ちを大切にしていますので、ご安心してご相談ください。',
  },
  {
    id: 5,
    question: '婚姻歴と子どもがおります。活動はできますでしょうか？',
    answer:
      'もちろん、可能です。\n同様にお子様いらっしゃる方や婚姻歴がある方も多数在籍されています。\nご安心ください。',
  },
];

interface AccordionItemProps {
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}

function AccordionItem({ item, isOpen, onToggle, index }: AccordionItemProps) {
  return (
    <div
      className="rounded-xl overflow-hidden cursor-pointer"
      style={{
        border: '2px solid',
        borderColor: isOpen ? '#5a9e6f' : 'rgba(90,158,111,0.25)',
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
        boxShadow: isOpen ? '0 4px 20px rgba(90,158,111,0.15)' : '0 2px 8px rgba(0,0,0,0.04)',
      }}
    >
      {/* Question row */}
      <button
        className="w-full text-left flex items-start gap-3 p-5 md:p-8"
        style={{
          background: isOpen ? '#f2f8f4' : '#fff',
          transition: 'background 0.3s ease',
        }}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        {/* Q label */}
        <span
          className="flex-shrink-0 flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full font-bold text-white text-sm md:text-base"
          style={{
            background: isOpen ? '#5a9e6f' : '#e8934e',
            transition: 'background 0.3s ease',
            minWidth: '36px',
          }}
        >
          Q
        </span>

        <span
          className="flex-1 font-bold pt-1"
          style={{ color: '#4a3728', fontSize: 'clamp(15px, 2.5vw, 18px)', lineHeight: '1.6' }}
        >
          {item.question}
        </span>

        {/* Toggle icon */}
        <span
          className="flex-shrink-0 flex items-center justify-center w-7 h-7 md:w-8 md:h-8 mt-1"
          style={{
            color: isOpen ? '#5a9e6f' : '#c8a882',
            transition: 'transform 0.35s ease, color 0.3s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <i className="ri-arrow-down-s-line text-xl md:text-2xl"></i>
        </span>
      </button>

      {/* Answer row */}
      <div
        style={{
          maxHeight: isOpen ? '600px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.45s ease',
        }}
      >
        <div
          className="flex items-start gap-3 px-5 md:px-8 pb-6 md:pb-8 pt-2"
          style={{ background: '#fff' }}
        >
          {/* A label */}
          <span
            className="flex-shrink-0 flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full font-bold text-sm md:text-base"
            style={{
              background: '#f2f8f4',
              color: '#5a9e6f',
              border: '2px solid #5a9e6f',
              minWidth: '36px',
            }}
          >
            A
          </span>

          <p
            className="flex-1 pt-1 whitespace-pre-line"
            style={{ color: '#5a4a3e', fontSize: 'clamp(14px, 2vw, 17px)', lineHeight: '2.0' }}
          >
            {item.answer}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FaqAccordionSection() {
  const [openId, setOpenId] = useState<number | null>(null);

  const handleToggle = (id: number) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <section className="section-padding" style={{ background: '#fff8f2' }}>
      <div className="content-width">
        <FadeInSection className="text-center mb-14">
          <p
            className="inline-block px-4 py-1 rounded-full text-sm font-bold mb-4"
            style={{ background: 'rgba(90,158,111,0.12)', color: '#5a9e6f' }}
          >
            FAQ
          </p>
          <h2 style={{ color: '#8b4513' }}>よくあるご質問</h2>
          <p className="mt-4 mx-auto max-w-xl" style={{ color: '#6b5a4e', fontSize: '17px', lineHeight: '1.8' }}>
            入会前のご不安・疑問をまとめました。<br className="hidden sm:block" />
            その他のご質問もお気軽にお問い合わせください。
          </p>
        </FadeInSection>

        <div className="max-w-3xl mx-auto flex flex-col gap-5">
          {faqItems.map((item, index) => (
            <FadeInSection key={item.id} delay={index * 80}>
              <AccordionItem
                item={item}
                isOpen={openId === item.id}
                onToggle={() => handleToggle(item.id)}
                index={index}
              />
            </FadeInSection>
          ))}
        </div>

        {/* Note */}
        <FadeInSection className="mt-12 text-center">
          <p style={{ color: '#8b7b6e', fontSize: '15px' }}>
            上記以外のご質問も、お気軽に
            <a href="/contact" style={{ color: '#e8934e', fontWeight: '600', textDecoration: 'underline', marginLeft: '4px', marginRight: '4px' }}>
              お問い合わせフォーム
            </a>
            またはお電話（03-4500-8399）にてご相談ください。
          </p>
        </FadeInSection>
      </div>
    </section>
  );
}
